import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth, ROLE_LABELS, FULL_ROLES } from "@/auth";
import {
  Users, MapPin, Link2, CheckCircle2, AlertCircle, Activity, Sparkles, Building2, Heart, Rocket, Flag, Lock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const ROLE_COLORS = {
  coord_estatal: "#0F172A",
  coord_municipal: "#2563EB",
  coord_regional: "#16A34A",
  coord_colonia: "#EAB308",
  admin_candidato: "#475569",
};

export default function DashboardOverview() {
  const { user, subaccount } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Cargando estadísticas…</div>;

  const isFull = FULL_ROLES.includes(user?.role);

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div>
        <span className="tracking-label text-blue-700">
          Operación · {ROLE_LABELS[user?.role]}
        </span>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-slate-900" data-testid="dashboard-title">
          {subaccount?.candidate_name || "Operación"}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Cobertura territorial en tiempo real ·{" "}
          {subaccount?.entidad_nombre}
          {subaccount?.municipio_nombre ? ` · ${subaccount.municipio_nombre}` : ""}
        </p>
      </div>

      {/* NOCTUA 360 Modules */}
      {isFull && <NoctuaModules subaccount={subaccount} />}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Voluntarios activos" value={stats.volunteers ?? 0} testid="kpi-volunteers" accent="slate" />
        <KPI
          icon={MapPin}
          label="Secciones con cobertura"
          value={`${stats.sections_covered || 0}${stats.sections_total ? ` / ${stats.sections_total}` : ""}`}
          testid="kpi-sections"
          accent="slate"
        />
        {isFull ? (
          <>
            <KPI icon={Link2} label="Enlaces totales" value={stats.links ?? 0} testid="kpi-links" accent="slate" />
            <KPI
              icon={AlertCircle}
              label="% Cobertura"
              value={`${(stats.coverage_pct ?? 0).toFixed(1)}%`}
              testid="kpi-coverage"
              accent="red"
            />
          </>
        ) : (
          <KPI icon={MapPin} label="Tu zona" value={stats.region_name || "—"} testid="kpi-region" accent="slate" />
        )}
      </div>

      {isFull && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top regions bar chart */}
          <div className="surface-card p-6 lg:col-span-2">
            <div className="tracking-label text-slate-500">Reclutamiento</div>
            <h2 className="mt-1 font-display font-bold text-xl text-slate-900">
              Top regiones por voluntarios
            </h2>
            {stats.top_regions && stats.top_regions.length > 0 ? (
              <div className="mt-6 h-72" data-testid="top-regions-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.top_regions} margin={{ top: 8, right: 16, left: -10, bottom: 28 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis
                      dataKey="region"
                      stroke="#64748B"
                      fontSize={11}
                      angle={-25}
                      textAnchor="end"
                      tickLine={false}
                      axisLine={{ stroke: "#E2E8F0" }}
                    />
                    <YAxis
                      stroke="#64748B"
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: "#E2E8F0" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0F172A",
                        border: "none",
                        borderRadius: "6px",
                        color: "#FFFFFF",
                        fontSize: "12px",
                      }}
                      cursor={{ fill: "rgba(37,99,235,0.06)" }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {stats.top_regions.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? "#DC2626" : "#0F172A"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart label="Aún no hay voluntarios registrados." />
            )}
          </div>

          {/* Distribution by role */}
          <div className="surface-card p-6">
            <div className="tracking-label text-slate-500">Distribución</div>
            <h2 className="mt-1 font-display font-bold text-xl text-slate-900">
              Voluntarios por rol asignado
            </h2>
            <div className="mt-6 space-y-4">
              {stats.volunteers_by_role && Object.keys(stats.volunteers_by_role).length > 0 ? (
                Object.entries(stats.volunteers_by_role).map(([role, count]) => {
                  const total = Object.values(stats.volunteers_by_role).reduce((a, b) => a + b, 0);
                  const pct = total ? (count / total) * 100 : 0;
                  return (
                    <div key={role} data-testid={`role-stat-${role}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700">{ROLE_LABELS[role] || role}</span>
                        <span className="font-display font-bold text-slate-900 tabular-nums">
                          {count} <span className="text-xs text-slate-500 font-normal">· {pct.toFixed(0)}%</span>
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 bg-slate-100 rounded overflow-hidden">
                        <div
                          className="h-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: ROLE_COLORS[role] || "#0F172A" }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyChart label="Sin distribución disponible." />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Coverage progress bar (full width) */}
      {isFull && stats.sections_total > 0 && (
        <div className="surface-card p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="tracking-label text-slate-500">Cobertura territorial</div>
              <h2 className="mt-1 font-display font-bold text-xl text-slate-900">
                Secciones electorales con al menos un voluntario
              </h2>
            </div>
            <span className="font-display font-black text-3xl text-blue-600" data-testid="coverage-pct">
              {stats.coverage_pct?.toFixed(1)}%
            </span>
          </div>
          <div className="mt-4 w-full bg-slate-100 rounded h-3 overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-700"
              style={{ width: `${Math.min(stats.coverage_pct || 0, 100)}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {stats.sections_covered} de {stats.sections_total} secciones cubiertas en el catálogo EDMSLM de esta subcuenta.
          </p>
        </div>
      )}

      {/* Recent activity */}
      {isFull && stats.recent_volunteers && stats.recent_volunteers.length > 0 && (
        <div className="surface-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
            <Activity className="w-4 h-4 text-slate-500" />
            <h2 className="font-display font-bold text-slate-900">Actividad reciente</h2>
          </div>
          <ul className="divide-y divide-slate-100">
            {stats.recent_volunteers.map((v, i) => (
              <li key={i} className="px-6 py-3 flex items-center justify-between text-sm" data-testid={`recent-row-${i}`}>
                <div>
                  <div className="font-medium text-slate-900">{v.full_name}</div>
                  <div className="text-xs text-slate-500">
                    Sección {v.seccion_electoral} · {v.colonia} · {v.target_region_name}
                  </div>
                </div>
                <div className="text-xs text-slate-500 tabular-nums">
                  {new Date(v.registered_at).toLocaleString("es-MX", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Coordinator region notice */}
      {!isFull && (
        <div className="surface-card p-6 bg-blue-50 border-blue-200">
          <CheckCircle2 className="w-6 h-6 text-blue-600" />
          <h3 className="mt-2 font-display font-bold text-lg text-slate-900">
            {stats.region_type === "colonia" ? "Tu colonia" : "Tu zona"}: {stats.region_name}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Solo verás los voluntarios y la cobertura registrados en {stats.region_name}.
          </p>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent = "slate", testid }) {
  const accentBar =
    accent === "red" ? "before:bg-red-600" :
    accent === "blue" ? "before:bg-blue-600" :
    "before:bg-slate-900";
  return (
    <div
      className={`surface-card p-5 relative overflow-hidden before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${accentBar}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between pl-2">
        <span className="tracking-label text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="mt-2 pl-2 font-display font-black text-3xl text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function EmptyChart({ label }) {
  return (
    <div className="h-48 flex items-center justify-center text-sm text-slate-400">
      {label}
    </div>
  );
}

const NOCTUA_MODULES = [
  {
    key: "viabilidad",
    num: "M1",
    title: "Viabilidad",
    desc: "Diagnóstico DVP · Arqueología territorial · Levantamiento estratégico",
    icon: Sparkles,
    to: "/dashboard/viabilidad",
    accent: "from-blue-500 to-indigo-600",
  },
  {
    key: "estructura",
    num: "M2",
    title: "Construcción de estructura",
    desc: "Voluntarios · Casillas · Cobertura · Usuarios · Enlaces OCR · Aprobaciones",
    icon: Building2,
    to: "/dashboard/estructura",
    accent: "from-slate-500 to-slate-700",
  },
  {
    key: "fidelizacion",
    num: "M3",
    title: "Fidelización",
    desc: "Programas de retención y activación de simpatizantes",
    icon: Heart,
    accent: "from-rose-500 to-red-600",
  },
  {
    key: "dispersion",
    num: "M4",
    title: "Dispersión",
    desc: "Cobertura territorial capilarizada",
    icon: Rocket,
    accent: "from-amber-500 to-orange-600",
  },
  {
    key: "dia_d",
    num: "M5",
    title: "Día D",
    desc: "Movilización, defensa del voto y cómputo",
    icon: Flag,
    accent: "from-emerald-500 to-teal-600",
  },
];

function NoctuaModules({ subaccount }) {
  const enabled = subaccount?.modules || {};
  const showLockedMsg = () => {
    alert("PONTE EN CONTACTO CON LOS ADMINISTRADORES DE NOCTUA 360 PARA HABILITAR ESTE MÓDULO");
  };

  return (
    <div className="surface-card p-6" data-testid="noctua-360-modules">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="tracking-label text-blue-700">NOCTUA 360</span>
          <h2 className="mt-1 font-display font-black text-xl text-slate-900">
            Módulos operativos
          </h2>
        </div>
        <Sparkles className="w-5 h-5 text-blue-600" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {NOCTUA_MODULES.map((m) => {
          const active = ["viabilidad", "estructura"].includes(m.key) ? true : !!enabled[m.key];
          const Icon = m.icon;
          const inner = (
            <div
              className={`group relative h-full rounded-lg border p-4 transition-all overflow-hidden ${
                active
                  ? "bg-white border-slate-200 hover:border-blue-500 hover:shadow-md cursor-pointer"
                  : "bg-slate-50 border-slate-200 cursor-pointer hover:border-slate-300"
              }`}
              data-testid={`noctua-module-${m.key}`}
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${m.accent} ${active ? "opacity-100" : "opacity-30"}`} />
              <div className="flex items-start justify-between">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-gradient-to-br ${m.accent} ${active ? "" : "opacity-40 grayscale"}`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                {!active && <Lock className="w-4 h-4 text-slate-400" />}
              </div>
              <div className="mt-3 text-[10px] tracking-widest text-slate-500 font-semibold">
                {m.num}
              </div>
              <div className={`font-display font-bold text-sm mt-0.5 ${active ? "text-slate-900" : "text-slate-500"}`}>
                {m.title}
              </div>
              <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                {m.desc}
              </div>
            </div>
          );
          if (active && m.to) {
            return <Link key={m.key} to={m.to}>{inner}</Link>;
          }
          return (
            <button
              key={m.key}
              onClick={showLockedMsg}
              className="text-left"
              data-testid={`noctua-module-${m.key}-locked`}
            >
              {inner}
            </button>
          );
        })}
      </div>
    </div>
  );
}
